// Short confirmations after an action, and heads-ups worth reading, announced
// politely to screen readers.
import { createContext, use, useCallback, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, X, XCircle } from 'lucide-react'
import { cn } from '../../lib/format.js'

const ToastContext = createContext(null)

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const counter = useRef(0)

  const dismiss = useCallback((id) => setToasts((list) => list.filter((t) => t.id !== id)), [])
  const push = useCallback(
    (tone, message) => {
      counter.current += 1
      const id = counter.current
      setToasts((list) => [...list.slice(-2), { id, tone, message }])
      setTimeout(() => dismiss(id), tone === 'success' ? 4000 : 7000)
    },
    [dismiss],
  )
  const api = useMemo(
    () => ({
      success: (message) => push('success', message),
      warning: (message) => push('warning', message),
      error: (message) => push('error', message),
    }),
    [push],
  )

  return (
    <ToastContext value={api}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[60] flex flex-col items-center gap-2 sm:items-end">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-xl bg-surface-2 px-4 py-3 text-sm shadow-2xl ring-1 shadow-black/50 ring-inset',
              t.tone === 'error' ? 'ring-red-500/40' : t.tone === 'warning' ? 'ring-amber-400/40' : 'ring-emerald-500/30',
            )}
          >
            {t.tone === 'error' ? (
              <XCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-red-300" />
            ) : t.tone === 'warning' ? (
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-amber-300" />
            ) : (
              <CheckCircle2 aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-emerald-300" />
            )}
            <p className="flex-1">{t.message}</p>
            <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss" className="rounded text-ink-muted hover:text-ink">
              <X aria-hidden="true" className="size-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext>
  )
}

export function useToast() {
  const value = use(ToastContext)
  if (!value) throw new Error('useToast must be used inside <ToastProvider>')
  return value
}
